import { createContext, useContext, useEffect, useMemo, useState } from "react";
import client from "../api/client";

const AuthContext = createContext(null);

function readUser() {
  try { return JSON.parse(localStorage.getItem("erpUser") || "null"); }
  catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readUser());
  const [token, setToken] = useState(localStorage.getItem("token"));

  const login = async (email, password, licenseKey = "") => {
    const res = await client.post("/auth/login", { email, password, licenseKey });
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("erpUser", JSON.stringify(res.data.user));
    if (res.data.user?.companyId) localStorage.setItem("companyId", String(res.data.user.companyId));
    else localStorage.removeItem("companyId");
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (payload) => {
    const res = await client.post("/auth/register", payload);
    return res.data;
  };

  const takeDatabaseBackup = async () => {
    const res = await client.post("/setup/backup-database", {}, { skipAuthRedirect: true });
    return res.data;
  };

  const logout = async ({ takeBackup = false } = {}) => {
    if (takeBackup) {
      try {
        const backup = await takeDatabaseBackup();
        alert(`Backup completed successfully.\nFolder: ${backup.folder || backup.backupPath || "Quanta"}`);
      } catch (e) {
        const proceed = window.confirm(`Backup failed: ${e.userMessage || e.message}.\nLogout anyway?`);
        if (!proceed) return;
      }
    }
    localStorage.removeItem("token");
    localStorage.removeItem("erpUser");
    localStorage.removeItem("companyId");
    setToken(null);
    setUser(null);
    window.location.href = "/login";
  };

  useEffect(() => {
    const handler = (event) => {
      if (!localStorage.getItem("token")) return;
      event.preventDefault();
      event.returnValue = "Do you want to close Quanta ERP? Please take database backup before closing.";
      return event.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const refreshUser = (patch) => {
    const updated = { ...(user || {}), ...patch };
    localStorage.setItem("erpUser", JSON.stringify(updated));
    if (updated?.companyId) localStorage.setItem("companyId", String(updated.companyId));
    else localStorage.removeItem("companyId");
    setUser(updated);
  };

  const hasPermission = (code) => {
    if (!code) return true;
    if (user?.roleName === "Admin") return true;
    return (user?.permissions || []).includes(code);
  };

  const value = useMemo(() => ({ user, token, isAuthenticated: !!token, login, register, logout, takeDatabaseBackup, refreshUser, hasPermission }), [user, token]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
